import React, { useCallback, useContext, useEffect, useState } from 'react'
import { Card } from '@themesberg/react-bootstrap'
import { Context } from '../SalesTicketStorage'
import { getSummaryClassicSalesTicket } from '../../../../controller/api/__classic__/ticketStorage'
import { useCookies } from 'react-cookie'
import { classicRegionName } from '../../../../helper/classicRegion'

const emptyKind = { sheets: 0, rows: 0 }

const formatCount = (value) => {
  const amount = Number(value)
  if (!Number.isFinite(amount)) return '0'
  return new Intl.NumberFormat('vi-VN').format(amount)
}

const kindText = (kind, unit) => `${formatCount(kind.rows)} ${unit} · ${formatCount(kind.sheets)} tờ`

const Metric = ({ label, kind, unit, tone, active }) => {
  const color = tone === 'pair' ? '#9A6700' : '#424AA0'
  const background = tone === 'pair' ? '#FFF6E8' : '#EBF4F6'

  return (
    <div
      style={{
        textAlign: 'left',
        border: active ? `1px solid ${color}` : '1px solid transparent',
        background,
        borderRadius: 8,
        padding: '8px 10px',
      }}
    >
      <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: 0.4, color }}>{label}</div>
      <div style={{ marginTop: 2 }}>
        <span style={{ fontSize: 18, fontWeight: 700, color: '#262B40' }}>{formatCount(kind.sheets)}</span>
        <span style={{ fontSize: 12, color: '#5a6275', marginLeft: 4 }}>tờ</span>
      </div>
      <div style={{ fontSize: 12, color: '#5a6275' }}>{formatCount(kind.rows)} {unit}</div>
    </div>
  )
}

const TypeButton = ({ label, kind, unit, active, onSelect }) => (
  <button
    type="button"
    onClick={onSelect}
    style={{
      flex: '1 1 220px',
      textAlign: 'left',
      border: active ? '2px solid #fff' : '2px solid transparent',
      background: active ? 'rgba(255,255,255,0.18)' : 'rgba(255,255,255,0.07)',
      borderRadius: 12,
      padding: '14px 16px',
      color: '#fff',
      cursor: 'pointer',
    }}
  >
    <div style={{ fontSize: 12, letterSpacing: 0.6, fontWeight: 700, opacity: 0.8 }}>{label}</div>
    <div style={{ fontSize: 28, fontWeight: 700, lineHeight: 1.2, marginTop: 4 }}>
      {formatCount(kind.sheets)}
      <span style={{ fontSize: 14, fontWeight: 600, marginLeft: 6 }}>tờ</span>
    </div>
    <div style={{ fontSize: 13, opacity: 0.85, marginTop: 2 }}>{formatCount(kind.rows)} {unit}</div>
  </button>
)

const _Summary = ({ region }) => {
  const { state, dispatch } = useContext(Context)
  const [cookies] = useCookies(['lottmekong'])
  const [summary, setSummary] = useState(null)
  const ticketType = state.ticketType === 'pair' ? 'pair' : 'single'

  const getSummaryFunc = useCallback(async () => {
    const response = await getSummaryClassicSalesTicket(cookies.lottmekong, {
      region,
    })

    if (response) {
      setSummary({
        single: response.single || emptyKind,
        pair: response.pair || emptyKind,
        dates: response.dates || [],
        region,
      })
    }
  }, [cookies.lottmekong, region])

  useEffect(() => {
    getSummaryFunc()
  }, [getSummaryFunc])

  const selectType = (nextType) => {
    dispatch({ type: 'SET_TICKET_TYPE', payload: nextType })
  }

  const visible = summary && summary.region === region ? summary : null
  const single = visible ? visible.single : emptyKind
  const pair = visible ? visible.pair : emptyKind
  const dates = visible ? visible.dates : []
  const totalSheets = single.sheets + pair.sheets

  return (
    <Card border="light" className="shadow-sm mb-4" style={{ borderRadius: 12, overflow: 'hidden' }}>
      <div style={{ padding: '18px 20px 16px', background: 'linear-gradient(135deg, #262B40 0%, #424AA0 100%)', color: '#fff' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16, alignItems: 'flex-end', marginBottom: 14 }}>
          <div>
            <div style={{ fontSize: 12, letterSpacing: 0.6, textTransform: 'uppercase', opacity: 0.75 }}>
              Thống kê vé đang bán
            </div>
            <div style={{ fontSize: 22, fontWeight: 700, marginTop: 4 }}>
              {classicRegionName(region)}
            </div>
          </div>
          <div style={{ textAlign: 'right' }}>
            <div style={{ fontSize: 12, opacity: 0.75 }}>Tổng số tờ còn bán</div>
            <div style={{ fontSize: 28, fontWeight: 700, lineHeight: 1.15 }}>{formatCount(totalSheets)}</div>
          </div>
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
          <TypeButton
            label="VÉ LẺ"
            kind={single}
            unit="dãy số"
            active={ticketType === 'single'}
            onSelect={() => selectType('single')}
          />
          <TypeButton
            label="CẶP NGUYÊN"
            kind={pair}
            unit="cặp"
            active={ticketType === 'pair'}
            onSelect={() => selectType('pair')}
          />
        </div>
        <div style={{ fontSize: 12, opacity: 0.75, marginTop: 10 }}>
          Bấm một nhóm để xem danh sách bên dưới.
        </div>
      </div>
      <Card.Body style={{ padding: 16 }}>
        {!visible ? (
          <div style={{ textAlign: 'center', color: '#6c757d', padding: '12px 0' }}>Đang tải thống kê</div>
        ) : dates.length === 0 ? (
          <div style={{ textAlign: 'center', color: '#6c757d', padding: '12px 0' }}>Chưa có vé đang bán</div>
        ) : dates.map((dateGroup) => (
          <div key={dateGroup.resultDate} style={{ marginBottom: 16 }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              gap: 12,
              flexWrap: 'wrap',
              background: '#EBF4F6',
              borderRadius: 8,
              padding: '8px 12px',
              marginBottom: 8,
            }}>
              <span style={{ fontWeight: 700, color: '#262B40' }}>{dateGroup.resultDate}</span>
              <span style={{ fontSize: 13, color: '#424AA0', fontWeight: 600 }}>
                Vé lẻ {kindText(dateGroup.single, 'dãy')} · Cặp nguyên {kindText(dateGroup.pair, 'cặp')}
              </span>
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              {dateGroup.stations.map((station) => (
                <div
                  key={`${dateGroup.resultDate}-${station.name}`}
                  style={{
                    flex: '1 1 260px',
                    maxWidth: 360,
                    border: '1px solid #e6eaf0',
                    borderRadius: 10,
                    padding: 12,
                    background: '#fff',
                  }}
                >
                  <div style={{ fontWeight: 700, color: '#262B40', marginBottom: 8 }}>{station.name}</div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                    <Metric
                      label="Vé lẻ"
                      kind={station.single}
                      unit="dãy"
                      tone="single"
                      active={ticketType === 'single'}
                    />
                    <Metric
                      label="Cặp nguyên"
                      kind={station.pair}
                      unit="cặp"
                      tone="pair"
                      active={ticketType === 'pair'}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </Card.Body>
    </Card>
  )
}

export default React.memo(_Summary)
